// src/services/prazosService.js
import { pb } from "./pocketbase";
import { SEED_PRAZOS_MODELO, SEED_TEMPOS_ETAPA } from "../data/prazosSeedData";
import { getModelFromSku } from "../utils/skuUtils";
import { getBusinessDaysBetween } from "../utils/formatters";

/**
 * Busca todos os prazos configurados por modelo na coleção oficina_prazos_modelo
 */
export async function getPrazos() {
  try {
    return await pb.collection("oficina_prazos_modelo").getFullList({
      sort: "modelo",
    });
  } catch (err) {
    console.error("Erro ao buscar oficina_prazos_modelo:", err);
    return [];
  }
}

/**
 * Salva alterações de um modelo (prazo prometido, médio ou status ativo)
 */
export async function savePrazo(id, data) {
  return await pb.collection("oficina_prazos_modelo").update(id, data);
}

/**
 * Cria um novo modelo na coleção oficina_prazos_modelo
 */
export async function createPrazo(data) {
  return await pb.collection("oficina_prazos_modelo").create(data);
}

/**
 * Busca todos os registros brutos da matriz de tempos por etapa
 */
export async function getTemposEtapa() {
  try {
    return await pb.collection("oficina_tempos_etapa").getFullList({
      sort: "modelo,etapa",
    });
  } catch (err) {
    console.error("Erro ao buscar oficina_tempos_etapa:", err);
    return [];
  }
}

/**
 * Retorna a matriz de tempos organizada como um mapa para consulta direta:
 * {
 *   "Triunfo": { "SOLICITACAO": 4.77, "IMPRIMINDO": 3.8, ... },
 *   "Escapulario": { "BANCA": 1.69, ... }
 * }
 */
export async function getTemposEtapaMap() {
  const records = await getTemposEtapa();
  const map = {};

  records.forEach((r) => {
    if (!r.modelo || !r.etapa) return;
    if (!map[r.modelo]) {
      map[r.modelo] = {};
    }
    map[r.modelo][r.etapa] = Number(r.dias_ate_conclusao) || 0;
  });

  return map;
}

/**
 * Carga inicial (Seed) segura e idempotente com dados da análise histórica.
 * Usa lógica de upsert — só atualiza ou cria, sem deletar nada.
 */
export async function seedInitialData(onProgress) {
  const prazosExistentes = await getPrazos();
  const prazosMap = new Map(prazosExistentes.map((p) => [p.modelo, p]));

  let processadosPrazos = 0;
  const totalPrazos = SEED_PRAZOS_MODELO.length;

  for (const item of SEED_PRAZOS_MODELO) {
    processadosPrazos++;
    if (onProgress) {
      onProgress(
        processadosPrazos,
        totalPrazos,
        `Gravando prazo do modelo ${item.modelo}...`
      );
    }

    const existente = prazosMap.get(item.modelo);
    if (existente) {
      await pb.collection("oficina_prazos_modelo").update(existente.id, {
        prazo_medio: item.prazo_medio,
        prazo_sugerido: item.prazo_sugerido,
        fator_risco: item.fator_risco,
      });
    } else {
      await pb.collection("oficina_prazos_modelo").create({
        modelo: item.modelo,
        prazo_prometido: item.prazo_prometido,
        prazo_medio: item.prazo_medio,
        prazo_sugerido: item.prazo_sugerido,
        fator_risco: item.fator_risco,
        ativo: true,
      });
    }
  }

  // Seed da Matriz de Tempos
  const temposExistentes = await getTemposEtapa();
  const temposMap = new Map(
    temposExistentes.map((t) => [`${t.modelo}__${t.etapa}`, t])
  );

  let processadosTempos = 0;
  const totalTempos = SEED_TEMPOS_ETAPA.length;

  for (const item of SEED_TEMPOS_ETAPA) {
    processadosTempos++;
    if (onProgress) {
      onProgress(
        processadosTempos,
        totalTempos,
        `Gravando tempo da etapa ${item.etapa} para ${item.modelo}...`
      );
    }

    const chave = `${item.modelo}__${item.etapa}`;
    const existente = temposMap.get(chave);

    if (existente) {
      await pb.collection("oficina_tempos_etapa").update(existente.id, {
        dias_ate_conclusao: item.dias_ate_conclusao,
        n_amostras: item.n_amostras,
      });
    } else {
      await pb.collection("oficina_tempos_etapa").create({
        modelo: item.modelo,
        etapa: item.etapa,
        dias_ate_conclusao: item.dias_ate_conclusao,
        n_amostras: item.n_amostras,
      });
    }
  }

  return { totalPrazos, totalTempos };
}

/**
 * Helper para calcular a mediana de um array de números
 */
function calcularMediana(valores) {
  if (!valores || valores.length === 0) return 0;
  const sorted = [...valores].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 !== 0
    ? sorted[mid]
    : (sorted[mid - 1] + sorted[mid]) / 2;
}

/**
 * Recalcula as sugestões de prazos com base nos pedidos concluídos no PocketBase
 */
export async function recalcularDoHistorico(onProgress) {
  if (onProgress) onProgress(0, 100, "Buscando pedidos concluídos no histórico...");

  // Busca pedidos finalizados
  const concluidos = await pb.collection("production_orders").getFullList({
    filter: 'status = "PEDIDO_PRONTO"',
    fields: "id,sku,created,updated,customCreatedAt",
  });

  if (concluidos.length === 0) {
    throw new Error("Nenhum pedido concluído encontrado para recálculo.");
  }

  // Agrupa leads (em dias úteis) por modelo
  const leadsPorModelo = {};

  concluidos.forEach((ord) => {
    const modelo = getModelFromSku(ord.sku);
    if (!modelo) return;

    const dataInicio = ord.customCreatedAt || ord.created;
    const dataFim = ord.updated;
    if (!dataInicio || !dataFim) return;

    const leadDias = getBusinessDaysBetween(dataInicio, dataFim);
    if (leadDias <= 0 || leadDias > 60) return; // descarta aberrações/outliers

    if (!leadsPorModelo[modelo]) {
      leadsPorModelo[modelo] = [];
    }
    leadsPorModelo[modelo].push(leadDias);
  });

  // Atualiza as sugestões na coleção oficina_prazos_modelo
  const prazosExistentes = await getPrazos();
  const prazosMap = new Map(prazosExistentes.map((p) => [p.modelo, p]));

  let atualizados = 0;
  for (const [modelo, leads] of Object.entries(leadsPorModelo)) {
    if (leads.length < 5) continue; // Confiabilidade mínima de 5 amostras

    const mediana = calcularMediana(leads);
    const sugerido = Math.ceil(mediana);
    const medio = Number((leads.reduce((a, b) => a + b, 0) / leads.length).toFixed(2));

    const existente = prazosMap.get(modelo);
    if (existente) {
      await pb.collection("oficina_prazos_modelo").update(existente.id, {
        prazo_sugerido: sugerido,
        prazo_medio: medio,
      });
      atualizados++;
    }
  }

  return { totalPedidosAnalisados: concluidos.length, modelosAtualizados: atualizados };
}
